import { defineStore } from 'pinia'
import { useAuthStore } from './auth.js'

const GUEST_CART_KEY = 'goldnaraGuestCart'
const MEMBER_CART_KEY = 'goldnaraMemberCart'

function readJson(storage, key, fallback) {
  try {
    return JSON.parse(storage.getItem(key) || JSON.stringify(fallback))
  } catch {
    return fallback
  }
}

function sameCartLine(a, b) {
  return a.productCode === b.productCode
    && (a.branchProductCode || '') === (b.branchProductCode || '')
    && String(a.weight || '') === String(b.weight || '')
    && String(a.purity || '') === String(b.purity || '')
}

export const useCartStore = defineStore('cart', {
  state: () => ({
    guestItems: readJson(sessionStorage, GUEST_CART_KEY, []),
    memberItems: readJson(localStorage, MEMBER_CART_KEY, []),
  }),
  getters: {
    items() {
      return useAuthStore().isLoggedIn ? this.memberItems : this.guestItems
    },
    checkedItems() {
      return this.items.filter(item => item.checked)
    },
    checkedCount() {
      return this.checkedItems.length
    },
    subtotal() {
      return this.checkedItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0), 0)
    },
  },
  actions: {
    persistGuest() {
      sessionStorage.setItem(GUEST_CART_KEY, JSON.stringify(this.guestItems))
    },
    persistMember() {
      localStorage.setItem(MEMBER_CART_KEY, JSON.stringify(this.memberItems))
    },
    persistCurrent() {
      if (useAuthStore().isLoggedIn) this.persistMember()
      else this.persistGuest()
    },
    addItem(item) {
      if (item?.priceDisplayType === 'CONTACT') {
        throw new Error('가격문의 상품은 장바구니에 담을 수 없습니다.')
      }
      const target = useAuthStore().isLoggedIn ? this.memberItems : this.guestItems
      const existing = target.find(row => sameCartLine(row, item))
      if (existing) {
        existing.qty = Number(existing.qty || 0) + Number(item.qty || 1)
        existing.checked = true
      } else {
        target.push({
          ...item,
          qty: Number(item.qty || 1),
          checked: item.checked !== false,
        })
      }
      this.persistCurrent()
    },
    updateQty(index, qty) {
      const target = this.items
      if (!target[index]) return
      target[index].qty = Math.max(1, Number(qty || 1))
      this.persistCurrent()
    },
    updateChecked(index, checked) {
      const target = this.items
      if (!target[index]) return
      target[index].checked = checked
      this.persistCurrent()
    },
    updatePaymentType(index, paymentType) {
      const target = this.items
      if (!target[index]) return
      target[index].selectedPaymentType = paymentType
      this.persistCurrent()
    },
    setAllChecked(checked) {
      this.items.forEach(item => {
        item.checked = checked
      })
      this.persistCurrent()
    },
    removeAt(index) {
      this.items.splice(index, 1)
      this.persistCurrent()
    },
    removeChecked() {
      if (useAuthStore().isLoggedIn) {
        this.memberItems = this.memberItems.filter(item => !item.checked)
        this.persistMember()
      } else {
        this.guestItems = this.guestItems.filter(item => !item.checked)
        this.persistGuest()
      }
    },
    mergeGuestIntoMember() {
      if (!this.guestItems.length) return
      for (const guestItem of this.guestItems) {
        const existing = this.memberItems.find(row => sameCartLine(row, guestItem))
        if (existing) {
          existing.qty = Number(existing.qty || 0) + Number(guestItem.qty || 1)
          existing.checked = existing.checked || guestItem.checked !== false
        } else {
          this.memberItems.push({ ...guestItem, checked: guestItem.checked !== false })
        }
      }
      this.guestItems = []
      sessionStorage.removeItem(GUEST_CART_KEY)
      this.persistMember()
    },
  },
})
